<template>
  <div
    id="app"
    :class="{ mobile: isMobile, light: lightTheme }"
    :style="{ overflow: !this.isMobile && this.enableSmoothScrollbar ? 'hidden' : '' }"
  >
    <TheMainMenu />

    <div
      class="app__content"
      ref="app"
      :style="{ height: !this.isMobile && this.enableSmoothScrollbar ? '100vh' : '' }"
    >
      <transition :name="transitionName" mode="out-in">
        <!-- WE HAVE RECIEVED DATA -->
        <router-view v-if="DATA_IS_LOADED" />

        <!-- WE HAVEN'T RECIEVED DATA -->
        <div class="loading" v-else>
          <!-- <p>LOADING</p> -->
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Scrollbar from "smooth-scrollbar";
import TheMainMenu from "@/components/TheMainMenu.vue";
import { setTimeout } from "timers";

const STANDARD_TRANSITION = "slide-out";

export default {
  components: {
    TheMainMenu
  },
  data() {
    return {
      resizeDebounceTimer: null,
      transitionName: STANDARD_TRANSITION,
      scrollBar: null,
      lastKnownYScroll: 0
    };
  },
  computed: mapState({
    DATA_IS_LOADED: state => state.data.isLoaded,
    isMobile: state => state.globals.isMobile,
    enableSmoothScrollbar: state => state.settings.enableSmoothScrollbar,
    smoothScrollbarInstance: state => state.settings.smoothScrollbarInstance,
    scrollDamping: state => state.settings.scrollDamping,
    currentScroll: state => state.globals.currentScroll,
    lightTheme: state => state.globals.lightTheme
  }),
  watch: {
    isMobile() {
      this.toggleScrollHandler(this.isMobile);
    }
    // DATA_IS_LOADED() {}
  },
  methods: {
    resizeCb() {
      if (this.resizeDebounceTimer) {
        window.clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = setTimeout(() => {
        this.$store.commit("SET_SCREEN_WIDTH", window.innerWidth);
        this.$store.commit("SET_SCREEN_HEIGHT", window.innerHeight);
        this.$store.commit("SET_SMALL_SCREEN", window.innerWidth <= 1024);
      }, 66);
    },

    scrollEventHandler() {
      const rAF = () => {
        if (this.lastKnownYScroll === window.pageYOffset) return;

        this.$store.commit("SET_CURRENT_SCROLL", window.pageYOffset);
        this.lastKnownYScroll = window.pageYOffset;
      };

      // window.removeEventListener('scroll', () => requestAnimationFrame(rAF), false)
      window.addEventListener(
        "scroll",
        () => requestAnimationFrame(rAF),
        false
      );
    },

    setupScrollbar() {
      if (!this.isMobile && this.enableSmoothScrollbar) {
        this.scrollBar = new Scrollbar.init(this.$refs.app, {
          damping: this.scrollDamping
        });
        this.scrollBar.addListener(status => {
          this.$store.commit("SET_CURRENT_SCROLL", status.offset.y);
        });

        // save it globally available if want to access it somewhere
        this.$store.commit("SET_SMOOTH_SCROLLBAR_INSTANCE", this.scrollBar);
      } else {
        this.scrollEventHandler();
      }

      // !this is currently to fast, and will always return 0
      // todo: only commit to store after 'window.pageYOffset' is calculated by the browser
      const scrollAmountOnPageLoad = this.scrollBar
        ? this.scrollBar.offset.y
        : window.pageYOffset;
      this.$store.commit("SET_CURRENT_SCROLL", scrollAmountOnPageLoad);
    }
  },
  created() {
    this.$router.beforeEach((to, from, next) => {
      // when going from one case to another,
      // vue doesn't know it should scroll back to the top.
      // because it's the same view file
      if (to.name === "case") {
        this.transitionName = "";
        setTimeout(scrollBackToTop.bind(this), 0);
      } else {
        this.transitionName = STANDARD_TRANSITION;
      }

      this.$store.commit("SET_HIDDEN_MENU", false);

      next();
    });

    this.$router.afterEach(() => {
      this.$nextTick(() => {
        this.$store.commit("SET_CURRENT_CASE", this.$route.params.url);
      });
    });

    function scrollBackToTop() {
      // On page change, scroll back to top
      // Because of the smooth scrollbar we have to disable the scroll transition real quick
      Object.assign(this.$el.children[1].style, {
        MozTransition: "none",
        transition: "none"
      });

      if (this.scrollBar) {
        this.scrollBar.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }

      this.$store.commit("SET_CURRENT_SCROLL", 0);

      // Activate the standard transition after page-transition is done
      setTimeout(
        () => this.$el.lastChild.style.removeProperty("transition"),
        600
      );
      setTimeout(
        () => this.$el.lastChild.style.removeProperty("-moz-transition"),
        600
      );
    }
  },
  beforeCreate() {
    // check if mobile browser
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      this.$store.commit("SET_IS_MOBILE", true);
    }
    this.$store.dispatch("GET_DATA");
  },
  mounted() {
    this.$store.commit("SET_SCREEN_WIDTH", window.innerWidth);
    this.$store.commit("SET_SCREEN_HEIGHT", window.innerHeight);
    this.$store.commit("SET_SMALL_SCREEN", window.innerWidth <= 1024);
    // this.$store.commit('SET_CURRENT_SCROLL', window.pageYOffset)

    this.$nextTick(this.setupScrollbar);

    window.addEventListener("resize", this.resizeCb);

    // google analytics track loading time
    // if (window.performance && this.$ga) {
    //   this.$ga.time(
    //     "Page done loading",
    //     "load",
    //     Math.round(window.performance.now())
    //   );
    // }
  }
};
</script>

<style lang="scss">
@import "/scss/globals.scss";

html {
  transition: background-color 0.6s linear;
  font-size: 100%;

  background-color: $color-black;
  color: $color-white;
}

body {
  transition: background-color 2s cubic-bezier(0.86, 0, 0.07, 1);
  margin: 0;
  font-family: $font-regular, sans-serif;
  line-height: $medium-line-height;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    color: currentColor;
    font-weight: 400;
    margin: 0;
  }

  a {
    color: currentColor;
    text-decoration: none;

    &:active,
    &:visited {
      color: currentColor;
    }
  }

  ul {
    padding: 0;
    margin: 0;
  }
}

.no-padding {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.no-padding-top {
  padding-top: 0 !important;
}
.no-padding-bottom {
  padding-bottom: 0 !important;
}

.view {
  box-sizing: border-box;
  width: 100vw;
  // overflow: hidden; // !breaks sticky elements
}

#app {
  transition: background-color 0.6s ease;

  // overflow-x: hidden;
  width: 100vw;

  &.mobile .app__content {
    height: auto;
  }

  &.light {
    background-color: $color-white;
    color: $color-black;

    #nav a.logo svg, #nav button.logo svg {
      fill: black;
    }
  }
}

.scrollbar-track {
  display: none !important;
}

*::selection {
  background: rgba(125, 125, 125, 0.2);
  color: currentColor;
}

// Global slide-show styling
.flickity {
  &-enabled {
    overflow: hidden !important;
    width: 100vw !important;
    margin: 0 !important;
  }

  &-enabled,
  &-viewport,
  &-slider {
    &:focus {
      outline: none;
    }
    > *:focus {
      outline: none;
    }
  }
}
</style>
